import React, { useState, useEffect } from 'react';
import {
    RefreshCw, UserCheck, Users,     ShieldAlert, ToggleLeft, ToggleRight, Key, Filter,
    LogOut, WrenchIcon, LogIn, Trash2, ShieldCheck, X, AlertTriangle, UserPlus
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Indicador de status online baseado em ULTIMO_ACESSO
// ---------------------------------------------------------------------------
const StatusOnline = ({ ultimoAcesso }) => {
    if (!ultimoAcesso) return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#cbd5e1' }} />
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>Offline</span>
        </div>
    );
    const mins = (Date.now() - new Date(ultimoAcesso).getTime()) / 60000;
    if (mins < 5) return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div className="animate-pulse" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
            <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>Online</span>
        </div>
    );
    if (mins < 30) return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
            <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600' }}>Ausente</span>
        </div>
    );
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#cbd5e1' }} />
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>Offline</span>
        </div>
    );
};

const AdminUsuarios = () => {
    const [usuariosAtivos, setUsuariosAtivos] = useState([]);
    const [stats, setStats] = useState({ total: 0, semAcesso: 0, inativos: 0 });
    const [manutencaoAtiva, setManutencaoAtiva] = useState(false);

    // TROCA DE ACESSO (perfil + localização)
    // As opções vêm do BACKEND (`/opcoes-acesso`), nunca de uma lista escrita aqui:
    // é o servidor quem valida, e duas listas separadas divergem calado — a tela
    // ofereceria um perfil que o `authorizeRoles` não conhece.
    const [opcoesAcesso, setOpcoesAcesso] = useState({ perfis: [], lojas: [] });
    const [editando,     setEditando]     = useState(null); // usuário em edição
    const [novoPerfil,   setNovoPerfil]   = useState('');
    const [novaLoja,     setNovaLoja]     = useState('');
    const [salvando,     setSalvando]     = useState(false);

    // CRIAR USUÁRIO
    // Até 19/08/2026 não havia nenhuma forma de criar usuário pelo portal — era
    // INSERT na mão no banco de produção.
    const VAZIO = { nome: '', usuario: '', cpf: '', email: '', perfil: '', loja: '' };
    const [criando,  setCriando]  = useState(false);
    const [novo,     setNovo]     = useState(VAZIO);
    const [erroNovo, setErroNovo] = useState('');

    // FILTROS
    const [filtroNome,        setFiltroNome]        = useState('');
    const [filtroPerfil,      setFiltroPerfil]      = useState('');
    const [filtroLocalizacao, setFiltroLocalizacao] = useState('');
    const [filtroPresenca,    setFiltroPresenca]    = useState('');
    const [filtroStatus,      setFiltroStatus]      = useState('');

    // ORDENAÇÃO
    const [sortField, setSortField] = useState(null);
    const [sortDir,   setSortDir]   = useState('asc');

    // FUNÇÕES DE BUSCA
    const fetchUsuariosCadastrados = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch('/api/cadastro/listar-todos', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                setUsuariosAtivos(data.usuarios || []);
                setStats(data.stats || { total: 0, semAcesso: 0, inativos: 0 });
            }
        } catch (error) {
            console.error("Erro ao buscar cadastrados", error);
        }
    };

    const fetchStatusSistema = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/status-sistema', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setManutencaoAtiva(data.manutencao);
        } catch (e) { /* silencioso */ }
    };

    const fetchOpcoesAcesso = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/opcoes-acesso', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setOpcoesAcesso({ perfis: data.perfis || [], lojas: data.lojas || [] });
        } catch (e) { /* silencioso — o botão de editar só abre com opções carregadas */ }
    };

    useEffect(() => {
        fetchUsuariosCadastrados();
        fetchStatusSistema();
        fetchOpcoesAcesso();
    }, []);

    const handleLogoutUsuario = async (id, nome) => {
        if (!window.confirm(`Deslogar ${nome}? O usuário será desconectado no próximo request.`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/cadastro/logout-usuario/${id}`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            alert(res.ok ? `${nome} foi deslogado.` : (data.message || 'Erro ao deslogar.'));
        } catch (e) { alert('Erro de conexão.'); }
    };

    const handleLogoutTodos = async () => {
        if (!window.confirm('Deslogar TODOS os usuários não-Admin? Eles serão desconectados no próximo request.')) return;
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/logout-todos', {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            alert(data.message);
        } catch (e) { alert('Erro de conexão.'); }
    };

    const handleToggleManutencao = async () => {
        const acao = manutencaoAtiva ? 'desativar' : 'ativar';
        if (!window.confirm(`Deseja ${acao} o modo manutenção? ${manutencaoAtiva ? 'Logins voltarão a funcionar.' : 'Novos logins (exceto Admin) serão bloqueados.'}`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/toggle-manutencao', {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) setManutencaoAtiva(data.manutencao);
            else alert(data.message || 'Erro ao alterar manutenção.');
        } catch (e) { alert('Erro de conexão.'); }
    };

    // LÓGICA DE FILTRAGEM DOS USUÁRIOS

    // Função auxiliar de presença (reutilizada no filtro e na ordenação)
    const getPresenca = (ultimoAcesso) => {
        if (!ultimoAcesso) return 'offline';
        const mins = (Date.now() - new Date(ultimoAcesso).getTime()) / 60000;
        if (mins < 5)  return 'ativo';
        if (mins < 30) return 'ausente';
        return 'offline';
    };

    const perfisUnicos       = [...new Set(usuariosAtivos.map(u => u.PERFIL))].filter(Boolean).sort();
    const localizacoesUnicas = [...new Set(usuariosAtivos.map(u => u.LOJA || ''))].filter(Boolean).sort();

    const usuariosFiltrados = usuariosAtivos.filter(u => {
        const matchNome        = filtroNome        === '' || u.NOME_COMPLETO.toLowerCase().includes(filtroNome.toLowerCase());
        const matchPerfil      = filtroPerfil      === '' || u.PERFIL === filtroPerfil;
        const matchLocalizacao = filtroLocalizacao === '' || (u.LOJA || '') === filtroLocalizacao;
        const matchPresenca    = filtroPresenca    === '' || getPresenca(u.ULTIMO_ACESSO) === filtroPresenca;
        const matchStatus      = filtroStatus      === '' || (filtroStatus === 'ativo' ? u.STATUS === 1 : u.STATUS !== 1);
        return matchNome && matchPerfil && matchLocalizacao && matchPresenca && matchStatus;
    });

    // Ordenação
    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return <span style={{ color: '#cbd5e1', fontSize: '10px' }}>↕</span>;
        return <span style={{ fontSize: '10px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    const usuariosOrdenados = [...usuariosFiltrados].sort((a, b) => {
        if (!sortField) return 0;
        let valA, valB;
        if (sortField === 'PRESENCA') {
            const ordem = { ativo: 0, ausente: 1, offline: 2 };
            valA = ordem[getPresenca(a.ULTIMO_ACESSO)];
            valB = ordem[getPresenca(b.ULTIMO_ACESSO)];
            return sortDir === 'asc' ? valA - valB : valB - valA;
        }
        if (sortField === 'STATUS') {
            valA = a.STATUS ?? 0; valB = b.STATUS ?? 0;
            return sortDir === 'asc' ? valA - valB : valB - valA;
        }
        valA = String(a[sortField] ?? '');
        valB = String(b[sortField] ?? '');
        const cmp = valA.localeCompare(valB, 'pt-BR', { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
    });

    // Limpar Filtros
    const limparFiltros = () => {
        setFiltroNome('');
        setFiltroPerfil('');
        setFiltroLocalizacao('');
        setFiltroPresenca('');
        setFiltroStatus('');
    };

    // FUNÇÕES DE AÇÃO
    const handleToggleStatus = async (id, statusAtual) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/cadastro/toggle-status/${id}`, {
                method: 'PATCH',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                setUsuariosAtivos(prev => prev.map(user => 
                    user.ID === id ? { ...user, STATUS: statusAtual === 1 ? 0 : 1 } : user
                ));
                fetchUsuariosCadastrados(); 
            } else {
                alert("Erro ao alterar status do usuário.");
            }
        } catch (error) {
            console.error("Erro na conexão:", error);
        }
    };

    const handleDeletarUsuario = async (id, nome) => {
        if (!window.confirm(`⚠️ ATENÇÃO: Deletar permanentemente o usuário "${nome}"?\n\nEsta ação é irreversível.`)) return;
        if (!window.confirm(`Confirme novamente: deletar "${nome}" do sistema?`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/cadastro/deletar-usuario/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) { alert(data.message); fetchUsuariosCadastrados(); }
            else alert(data.message || 'Erro ao deletar.');
        } catch (e) { alert('Erro de conexão.'); }
    };

    const handleResetSenha = async (id, nome) => {
        if (!window.confirm(`Redefinir a senha de ${nome} para a senha padrão?\nO usuário será obrigado a alterá-la no próximo login.`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/cadastro/reset-senha-padrao/${id}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            alert(data.message);
            fetchUsuariosCadastrados();
        } catch (e) {
            alert('Erro ao redefinir senha.');
        }
    };

    // Sugestão de login a partir do nome: `primeiro.ultimo`, sem acento e sem as
    // partículas ("de", "da", "dos"). Espelha `server/paineladmin/novo-usuario.js`
    // — é só conveniência de digitação; quem valida é o servidor.
    const sugerirUsuario = (nomeCompleto) => {
        const partes = String(nomeCompleto || '')
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .toLowerCase().replace(/[^a-z\s]/g, ' ')
            .split(/\s+/).filter(p => p.length > 2);
        if (!partes.length) return '';
        return partes.length === 1 ? partes[0] : `${partes[0]}.${partes[partes.length - 1]}`;
    };

    const abrirCriacao = () => {
        setNovo({ ...VAZIO, loja: opcoesAcesso.lojas[0] || '' });
        setErroNovo('');
        setCriando(true);
    };

    // O nome preenche o login enquanto o Admin não mexer nele à mão — mexeu,
    // paramos de sobrescrever (senão a tela desfaz o que a pessoa acabou de digitar).
    const mudarNome = (valor) => {
        setNovo(n => ({
            ...n,
            nome: valor,
            usuario: (!n.usuario || n.usuario === sugerirUsuario(n.nome)) ? sugerirUsuario(valor) : n.usuario,
        }));
    };

    const handleCriarUsuario = async () => {
        setSalvando(true);
        setErroNovo('');
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/usuario', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(novo)
            });
            const data = await res.json();
            if (!res.ok) { setErroNovo(data.message || 'Erro ao criar o usuário.'); return; }
            alert(data.message);
            setCriando(false);
            fetchUsuariosCadastrados();
            fetchOpcoesAcesso();
        } catch (e) {
            setErroNovo('Erro de conexão.');
        } finally {
            setSalvando(false);
        }
    };

    // Abre o modal já com os valores de hoje selecionados.
    const abrirEdicaoAcesso = (u) => {
        setEditando(u);
        setNovoPerfil(u.PERFIL || '');
        setNovaLoja(u.LOJA || '');
    };

    const handleSalvarAcesso = async () => {
        if (!editando) return;
        setSalvando(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/cadastro/usuario/${editando.ID}/acesso`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ perfil: novoPerfil, loja: novaLoja })
            });
            const data = await res.json();
            alert(data.message || (res.ok ? 'Acesso atualizado.' : 'Erro ao salvar.'));
            if (res.ok) {
                setEditando(null);
                fetchUsuariosCadastrados();
            }
        } catch (e) {
            alert('Erro de conexão.');
        } finally {
            setSalvando(false);
        }
    };

    // O perfil atual pode não estar na lista canônica (cadastro antigo, grafia
    // torta). Nesse caso ele entra no select mesmo assim — senão o <select> abriria
    // vazio e esconderia justamente o cadastro que precisa ser consertado.
    const perfisDoSelect = editando && editando.PERFIL && !opcoesAcesso.perfis.includes(editando.PERFIL)
        ? [editando.PERFIL, ...opcoesAcesso.perfis]
        : opcoesAcesso.perfis;

    const houveMudanca = !!editando && (novoPerfil !== (editando.PERFIL || '') || novaLoja !== (editando.LOJA || ''));

    // ⚠️ Aviso, não trava: o recorte por loja sai da LOJA do cadastro. Gerente
    //    lotado no escritório enxerga a rede toda — existe caso legítimo, então o
    //    servidor deixa passar e a tela só chama a atenção.
    const avisoLojaGerente = !!editando
        && ['Gerente', 'Gerente Adm', 'Gerente Plantao'].includes(novoPerfil)
        && novaLoja === 'ESCRITÓRIO';

    const isSenhaExpirada = (dataSenha) => {
        if (!dataSenha) return true;
        const data = new Date(dataSenha);
        const hoje = new Date();
        const diffTime = Math.abs(hoje - data);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 90;
    };

    // COMPONENTES AUXILIARES E ESTILOS
    const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', borderLeft: `4px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
                <p style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', fontWeight: '800', margin: '0 0 4px 0' }}>{title}</p>
                <p style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{value}</p>
            </div>
            <div style={{ padding: '12px', borderRadius: '50%', backgroundColor: bgColor, color: color }}>
                <Icon size={20} />
            </div>
        </div>
    );

    const tableStyle = { width: '100%', borderCollapse: 'collapse', textAlign: 'left' };
    const theadStyle = { backgroundColor: '#f8fafc', color: '#64748b', fontSize: '12px', textTransform: 'uppercase' };
    const thStyle = { padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0' };
    const tdStyle = { padding: '16px 24px', fontSize: '14px', borderBottom: '1px solid #f1f5f9' };
    const trBodyStyle = { transition: 'background-color 0.2s' };

    // Estilos para os filtros
    const filterSelectStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', minWidth: '150px' };
    const filterLabelStyle = { fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' };

    return (
        <div>
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '12px', color: '#3b82f6' }}>
                        <Users size={28} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px 0' }}>Gestão de Usuários</h2>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Controle de acessos e segurança da plataforma.</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={abrirCriacao}
                        title="Cadastrar um novo usuário no portal"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: '#fff' }}
                    >
                        <UserPlus size={15} /> Novo usuário
                    </button>
                    <button
                        onClick={() => { fetchUsuariosCadastrados(); fetchStatusSistema(); }}
                        title="Recarregar lista de usuários"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                    >
                        <RefreshCw size={15} /> Atualizar
                    </button>
                    <button
                        onClick={handleLogoutTodos}
                        title="Encerra a sessão de todos os usuários não-Admin. Eles serão desconectados no próximo request."
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: '#e11d48' }}
                    >
                        <LogOut size={15} /> Deslogar Todos
                    </button>
                    <button
                        onClick={handleToggleManutencao}
                        title={manutencaoAtiva ? 'Desativar modo manutenção — libera novos logins' : 'Ativar modo manutenção — bloqueia novos logins (exceto Admin)'}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: manutencaoAtiva ? '#fff7ed' : '#f8fafc', border: `1px solid ${manutencaoAtiva ? '#fed7aa' : '#e2e8f0'}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', color: manutencaoAtiva ? '#c2410c' : '#64748b' }}
                    >
                        {manutencaoAtiva ? <LogIn size={15} /> : <WrenchIcon size={15} />}
                        {manutencaoAtiva ? 'Desativar Manutenção' : 'Modo Manutenção'}
                    </button>
                </div>
            </div>

            {/* KPIs */}
            {(() => {
                const ativos24h = usuariosAtivos.filter(u => {
                    if (!u.ULTIMO_ACESSO) return false;
                    return (Date.now() - new Date(u.ULTIMO_ACESSO).getTime()) <= 24 * 60 * 60 * 1000;
                }).length;
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                        <StatCard title="Ativos nas últimas 24h" value={ativos24h}    icon={UserCheck}  color="#10b981" bgColor="#ecfdf5" />
                        <StatCard title="Bloqueados"             value={stats.inativos} icon={ShieldAlert} color="#f43f5e" bgColor="#fff1f2" />
                    </div>
                );
            })()}


            {/* BARRA DE FILTROS */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569', alignSelf: 'center', marginRight: '4px' }}>
                    <Filter size={16} />
                    <span style={{ fontWeight: '700', fontSize: '13px' }}>Filtros</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', flex: '1', minWidth: '160px' }}>
                    <label style={filterLabelStyle}>Nome</label>
                    <input
                        type="text"
                        placeholder="Buscar por nome..."
                        value={filtroNome}
                        onChange={e => setFiltroNome(e.target.value)}
                        style={{ ...filterSelectStyle, minWidth: '180px' }}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={filterLabelStyle}>Perfil</label>
                    <select value={filtroPerfil} onChange={e => setFiltroPerfil(e.target.value)} style={filterSelectStyle}>
                        <option value="">Todos</option>
                        {perfisUnicos.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={filterLabelStyle}>Localização</label>
                    <select value={filtroLocalizacao} onChange={e => setFiltroLocalizacao(e.target.value)} style={filterSelectStyle}>
                        <option value="">Todas</option>
                        {localizacoesUnicas.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={filterLabelStyle}>Status</label>
                    <select value={filtroPresenca} onChange={e => setFiltroPresenca(e.target.value)} style={filterSelectStyle}>
                        <option value="">Todos</option>
                        <option value="ativo">🟢 Online</option>
                        <option value="ausente">🟡 Ausente</option>
                        <option value="offline">⚫ Offline</option>
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={filterLabelStyle}>Situação</label>
                    <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={filterSelectStyle}>
                        <option value="">Todos</option>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                    </select>
                </div>

                {(filtroNome || filtroPerfil || filtroLocalizacao || filtroPresenca || filtroStatus) && (
                    <button onClick={limparFiltros} style={{ padding: '8px 14px', fontSize: '12px', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                        Limpar
                    </button>
                )}
            </div>

            {/* TABELA DE UTILIZADORES */}
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={tableStyle}>
                        <thead style={theadStyle}>
                            <tr>
                                {[
                                    { label: 'Utilizador',    field: 'NOME_COMPLETO'  },
                                    { label: 'Perfil',        field: 'PERFIL'         },
                                    { label: 'Localização',   field: 'LOJA'           },
                                    { label: 'Status',        field: 'PRESENCA'       },
                                    { label: 'Situação',      field: 'STATUS'         },
                                    { label: 'Última Senha',  field: 'DATA_ULTIMA_SENHA' },
                                    { label: 'Último Acesso', field: 'ULTIMO_ACESSO'  },
                                ].map(({ label, field }) => (
                                    <th key={field} onClick={() => toggleSort(field)}
                                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {label} {sortIcon(field)}
                                        </div>
                                    </th>
                                ))}
                                <th style={thStyle}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuariosFiltrados.length === 0 ? (
                                <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nenhum utilizador encontrado com esses filtros.</td></tr>
                            ) : (
                                usuariosOrdenados.map(u => (
                                    <tr key={u.ID} style={trBodyStyle}>
                                        <td style={tdStyle}>
                                            <strong>{u.NOME_COMPLETO}</strong>
                                        </td>
                                        <td style={tdStyle}>{u.PERFIL}</td>
                                        <td style={tdStyle}>{u.LOJA || '—'}</td>
                                        <td style={tdStyle}><StatusOnline ultimoAcesso={u.ULTIMO_ACESSO} /></td>
                                        <td style={tdStyle}>
                                            <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', backgroundColor: u.STATUS === 1 ? '#dcfce7' : '#fee2e2', color: u.STATUS === 1 ? '#166534' : '#991b1b' }}>
                                                {u.STATUS === 1 ? 'ATIVO' : 'INATIVO'}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <small style={{ color: isSenhaExpirada(u.DATA_ULTIMA_SENHA) ? '#f43f5e' : '#64748b' }}>
                                                {u.DATA_ULTIMA_SENHA ? new Date(u.DATA_ULTIMA_SENHA).toLocaleDateString() : 'N/A'}
                                            </small>
                                        </td>
                                        <td style={tdStyle}>
                                            <small style={{ color: '#64748b' }}>
                                                {u.ULTIMO_ACESSO
                                                    ? new Date(u.ULTIMO_ACESSO).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                    : <span style={{ color: '#cbd5e1' }}>—</span>
                                                }
                                            </small>
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <button
                                                    onClick={() => abrirEdicaoAcesso(u)}
                                                    title="Alterar perfil e localização — encerra a sessão do usuário"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                                                >
                                                    <ShieldCheck size={17} color="#8b5cf6" />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(u.ID, u.STATUS)}
                                                    title={u.STATUS === 1 ? 'Desativar conta — bloqueia o acesso do usuário' : 'Ativar conta — libera o acesso do usuário'}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                                                >
                                                    {u.STATUS === 1 ? <ToggleRight size={22} color="#10b981" /> : <ToggleLeft size={22} color="#94a3b8" />}
                                                </button>
                                                <button
                                                    onClick={() => handleResetSenha(u.ID, u.NOME_COMPLETO)}
                                                    title="Redefinir senha para o padrão — usuário deverá alterá-la no próximo login"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                                                >
                                                    <Key size={17} color="#3b82f6" />
                                                </button>
                                                <button
                                                    onClick={() => handleLogoutUsuario(u.ID, u.NOME_COMPLETO)}
                                                    title="Encerrar sessão — o usuário será desconectado no próximo request"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                                                >
                                                    <LogOut size={16} color="#f59e0b" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletarUsuario(u.ID, u.NOME_COMPLETO)}
                                                    title="Deletar usuário permanentemente — ação irreversível"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}
                                                >
                                                    <Trash2 size={16} color="#e11d48" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
            </div>

            {/* MODAL — NOVO USUÁRIO */}
            {criando && (
                <div
                    onClick={() => !salvando && setCriando(false)}
                    style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ padding: '10px', backgroundColor: '#eff6ff', borderRadius: '10px', color: '#3b82f6', display: 'flex' }}>
                                    <UserPlus size={20} />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Novo usuário</h3>
                                    <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>Cadastro de acesso ao portal</p>
                                </div>
                            </div>
                            <button onClick={() => setCriando(false)} disabled={salvando}
                                style={{ background: 'none', border: 'none', cursor: salvando ? 'default' : 'pointer', color: '#94a3b8', padding: '4px', display: 'flex' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <div style={{ padding: '24px', overflowY: 'auto' }}>
                            <label style={{ ...filterLabelStyle, display: 'block' }}>Nome completo</label>
                            <input
                                value={novo.nome}
                                onChange={e => mudarNome(e.target.value)}
                                placeholder="JOAO DA SILVA SANTOS"
                                style={{ ...filterSelectStyle, width: '100%', marginBottom: '16px', boxSizing: 'border-box' }}
                            />

                            <label style={{ ...filterLabelStyle, display: 'block' }}>Usuário (login)</label>
                            <input
                                value={novo.usuario}
                                onChange={e => setNovo(n => ({ ...n, usuario: e.target.value }))}
                                placeholder="joao.santos"
                                style={{ ...filterSelectStyle, width: '100%', marginBottom: '4px', boxSizing: 'border-box' }}
                            />
                            <div style={{ fontSize: '11.5px', color: '#94a3b8', marginBottom: '16px' }}>
                                Sugerido a partir do nome. Minúsculas, sem espaço nem acento.
                            </div>

                            <label style={{ ...filterLabelStyle, display: 'block' }}>CPF</label>
                            <input
                                value={novo.cpf}
                                onChange={e => setNovo(n => ({ ...n, cpf: e.target.value }))}
                                placeholder="000.000.000-00"
                                inputMode="numeric"
                                style={{ ...filterSelectStyle, width: '100%', marginBottom: '4px', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }}
                            />
                            {/* ⚠️ Não é burocracia: o "Esqueci a senha" acha a pessoa POR CPF.
                                Sem ele a pessoa nunca recupera a senha sozinha — e, como
                                aquela tela responde de forma neutra, não recebe erro: recebe
                                silêncio, e vai achar que o portal está quebrado. */}
                            <div style={{ fontSize: '11.5px', color: '#94a3b8', marginBottom: '16px' }}>
                                Obrigatório — é por ele que o "Esqueci a senha" encontra a pessoa.
                            </div>

                            <label style={{ ...filterLabelStyle, display: 'block' }}>E-mail <span style={{ color: '#94a3b8', fontWeight: '400' }}>(opcional)</span></label>
                            <input
                                value={novo.email}
                                onChange={e => setNovo(n => ({ ...n, email: e.target.value }))}
                                placeholder="joao.santos@mcdes.com.br"
                                style={{ ...filterSelectStyle, width: '100%', marginBottom: '16px', boxSizing: 'border-box' }}
                            />

                            <div style={{ display: 'flex', gap: '14px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ ...filterLabelStyle, display: 'block' }}>Perfil</label>
                                    <select
                                        value={novo.perfil}
                                        onChange={e => setNovo(n => ({ ...n, perfil: e.target.value }))}
                                        style={{ ...filterSelectStyle, width: '100%', boxSizing: 'border-box' }}
                                    >
                                        <option value="">Selecione…</option>
                                        {opcoesAcesso.perfis.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ ...filterLabelStyle, display: 'block' }}>Localização</label>
                                    <select
                                        value={novo.loja}
                                        onChange={e => setNovo(n => ({ ...n, loja: e.target.value }))}
                                        style={{ ...filterSelectStyle, width: '100%', boxSizing: 'border-box' }}
                                    >
                                        {opcoesAcesso.lojas.map(l => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* O erro fica DENTRO do modal, não num alert: o Admin precisa
                                ver qual campo corrigir sem perder o que já digitou. */}
                            {erroNovo && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '18px', padding: '12px 14px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px' }}>
                                    <AlertTriangle size={16} color="#be123c" style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span style={{ fontSize: '12.5px', color: '#9f1239', lineHeight: 1.5 }}>{erroNovo}</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                <Key size={16} color="#64748b" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5 }}>
                                    A conta nasce com a <strong>senha padrão do portal</strong> (a mesma do botão
                                    "Redefinir senha") e o usuário é <strong>obrigado a trocá-la no primeiro acesso</strong>.
                                    Ninguém digita senha por outra pessoa aqui.
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                            <button onClick={() => setCriando(false)} disabled={salvando}
                                style={{ padding: '10px 18px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: salvando ? 'default' : 'pointer', fontWeight: '600', fontSize: '13px', color: '#475569' }}>
                                Cancelar
                            </button>
                            <button
                                onClick={handleCriarUsuario}
                                disabled={salvando}
                                style={{ padding: '10px 18px', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13px', color: '#fff', backgroundColor: salvando ? '#93c5fd' : '#3b82f6', cursor: salvando ? 'default' : 'pointer' }}
                            >
                                {salvando ? 'Criando…' : 'Criar usuário'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL — TROCA DE ACESSO */}
            {editando && (
                <div
                    onClick={() => !salvando && setEditando(null)}
                    style={{
                        position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '460px',
                            border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)', overflow: 'hidden'
                        }}
                    >
                        {/* Cabeçalho */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ padding: '10px', backgroundColor: '#f5f3ff', borderRadius: '10px', color: '#8b5cf6', display: 'flex' }}>
                                    <ShieldCheck size={20} />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Alterar acesso</h3>
                                    <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>{editando.NOME_COMPLETO}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setEditando(null)}
                                disabled={salvando}
                                style={{ background: 'none', border: 'none', cursor: salvando ? 'default' : 'pointer', color: '#94a3b8', padding: '4px', display: 'flex' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Corpo */}
                        <div style={{ padding: '24px' }}>
                            <label style={{ ...filterLabelStyle, display: 'block' }}>Perfil</label>
                            <select
                                value={novoPerfil}
                                onChange={e => setNovoPerfil(e.target.value)}
                                style={{ ...filterSelectStyle, width: '100%', marginBottom: '18px' }}
                            >
                                {perfisDoSelect.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>

                            <label style={{ ...filterLabelStyle, display: 'block' }}>Localização</label>
                            <select
                                value={novaLoja}
                                onChange={e => setNovaLoja(e.target.value)}
                                style={{ ...filterSelectStyle, width: '100%' }}
                            >
                                {opcoesAcesso.lojas.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>

                            {avisoLojaGerente && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '18px', padding: '12px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px' }}>
                                    <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span style={{ fontSize: '12.5px', color: '#92400e', lineHeight: 1.5 }}>
                                        Gerente no <strong>ESCRITÓRIO</strong> deixa de ficar preso a uma loja — o recorte por
                                        loja passa a valer para a rede inteira. Confirme se é isso mesmo.
                                    </span>
                                </div>
                            )}

                            {/* ⚠️ A frase abaixo não é enfeite: é o que a pessoa precisa saber ANTES
                                de clicar. O perfil viaja dentro do token, então a troca só vale
                                depois que a sessão cai — e ela cai na hora, de propósito. */}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                <LogOut size={16} color="#64748b" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5 }}>
                                    Ao salvar, a <strong>sessão do usuário é encerrada</strong>. O perfil viaja dentro do
                                    token de login: sem isso, a mudança só valeria no próximo acesso dele.
                                </span>
                            </div>
                        </div>

                        {/* Rodapé */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                            <button
                                onClick={() => setEditando(null)}
                                disabled={salvando}
                                style={{ padding: '10px 18px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: salvando ? 'default' : 'pointer', fontWeight: '600', fontSize: '13px', color: '#475569' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSalvarAcesso}
                                disabled={salvando || !houveMudanca}
                                title={houveMudanca ? 'Salvar e encerrar a sessão do usuário' : 'Nada foi alterado'}
                                style={{
                                    padding: '10px 18px', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13px', color: '#fff',
                                    backgroundColor: (salvando || !houveMudanca) ? '#c4b5fd' : '#8b5cf6',
                                    cursor: (salvando || !houveMudanca) ? 'default' : 'pointer'
                                }}
                            >
                                {salvando ? 'Salvando…' : 'Salvar acesso'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsuarios;